import {ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, OnDestroy, output, signal} from "@angular/core"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {MatDividerModule} from "@angular/material/divider"
import {MatMenuModule} from "@angular/material/menu"
import {MatTooltipModule} from "@angular/material/tooltip"
import {DownloadResolution, ImageColorSpace} from "@generated"
import {AuthService} from "@app/common/services/auth/auth.service"
import {
    TextureThumbnailViewDataObjectDetailsFragment,
    TextureThumbnailViewDataObjectFragment,
    TextureThumbnailViewQueryDataObjectGQL,
    TextureThumbnailViewUpdateDataObjectGQL,
} from "@app/textures/texture-set-revision-view/texture-thumbnail-view/texture-thumbnail-view.generated"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {mutateThrowingErrors} from "@common/helpers/api/mutate"
import {Settings} from "@common/models/settings/settings"
import {FilesService} from "@common/services/files/files.service"
import {OrganizationsService} from "@common/services/organizations/organizations.service"
import {interval, Subject, takeUntil} from "rxjs"
import {ThumbnailComponent} from "@common/components/thumbnail/thumbnail.component"
import {DataObjectThumbnailComponent} from "@common/components/data-object-thumbnail/data-object-thumbnail.component"
import {ThumbnailLayout} from "@common/models/enums/thumbnail-layout"
import {MimeType} from "@legacy/helpers/utils"
import {DropFilesComponent} from "@common/components/files"
import {PermissionsService} from "@common/services/permissions/permissions.service"
import {deepCopy} from "@cm/utils"
import {GenerateThumbnailService} from "@common/services/generate-thumbnail/generate-thumbnail.service"

const POLL_THUMBNAIL_TIMEOUT = 1000

const MEDIA_TYPE_SORT_ORDER: Record<string, number> = {
    "image/tiff": 0,
    "image/jpeg": 1,
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-texture-thumbnail-view",
    templateUrl: "./texture-thumbnail-view.component.html",
    styleUrl: "./texture-thumbnail-view.component.scss",
    imports: [MatTooltipModule, MatMenuModule, MatDividerModule, ThumbnailComponent, DataObjectThumbnailComponent, DropFilesComponent],
})
export class TextureThumbnailViewComponent implements OnDestroy {
    readonly $label = input("", {alias: "label"})

    readonly $dataObjectId = input<string | undefined>(undefined, {alias: "dataObjectId"})

    readonly $isUploading = input(false, {alias: "isUploading"})
    readonly $canDrop = input(false, {alias: "canDrop"})
    readonly $canDelete = input(false, {alias: "canDelete"})
    readonly $canDownload = input(false, {alias: "canDownload"})

    readonly load = output<Event>()
    readonly thumbnailAvailable = output<TextureThumbnailViewDataObjectDetailsFragment>()
    readonly fileDropped = output<File>()
    readonly deleteClicked = output<void>()

    readonly permission = inject(PermissionsService)
    readonly organization = inject(OrganizationsService)
    $can = this.permission.$to

    readonly textureThumbnailViewQueryDataObject = inject(TextureThumbnailViewQueryDataObjectGQL)
    readonly textureThumbnailViewUpdateDataObject = inject(TextureThumbnailViewUpdateDataObjectGQL)

    protected readonly $mouseHoovering = signal<boolean>(false)
    protected readonly $showDropzone = computed(() => this.$mouseHoovering() && this.$canDrop())
    private readonly destroyRef = inject(DestroyRef)
    protected readonly authService = inject(AuthService)
    private readonly generateThumbnailService = inject(GenerateThumbnailService)

    constructor() {
        effect(() => {
            const dataObjectId = this.$dataObjectId()
            void this.loadDataObject(dataObjectId)
        })
    }

    ngOnDestroy(): void {
        this._loadingNewTexture.complete()
        this._dataObjectUpdated.complete()
        this._isDestroyed = true
    }

    private async loadDataObject(dataObjectId: string | undefined) {
        if (this._dataObjectId === dataObjectId) {
            return
        }
        this._loadingNewTexture.next()
        this._dataObjectId = dataObjectId
        if (this._dataObjectId) {
            try {
                const result = await fetchThrowingErrors(this.textureThumbnailViewQueryDataObject)({dataObjectId: this._dataObjectId})
                const dataObject = deepCopy(result.dataObject) // we make a copy here to create a writeable object as we might wanna change the color space
                this.$dataObject.set(dataObject)
                if (dataObject.thumbnailAvailable) {
                    // Prevent emitting the event after the component is destroyed
                    // FIXME: This loading logic should be refactored, so we can cancel the subscription instead.
                    if (!this._isDestroyed) {
                        this.thumbnailAvailable.emit(dataObject)
                    }
                } else {
                    interval(POLL_THUMBNAIL_TIMEOUT)
                        .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this._loadingNewTexture), takeUntil(this._dataObjectUpdated))
                        .subscribe(() => this.pollUpdatedDataObject(dataObject))
                }
            } catch {
                this.$dataObject.set(undefined)
            }
        } else {
            this.$dataObject.set(undefined)
        }
    }

    private async pollUpdatedDataObject(dataObject: TextureThumbnailViewDataObjectFragment) {
        const updatedDataObject = await fetchThrowingErrors(this.textureThumbnailViewQueryDataObject)({dataObjectId: dataObject.id})
        if (updatedDataObject.dataObject.thumbnailAvailable) {
            this.$dataObject.set(updatedDataObject.dataObject)
            this._dataObjectUpdated.next()
            this.thumbnailAvailable.emit(updatedDataObject.dataObject)
        }
    }

    protected colorSpaceIsUnknown(dataObject: TextureThumbnailViewDataObjectFragment): boolean {
        return (dataObject.imageColorSpace ?? ImageColorSpace.Unknown) === ImageColorSpace.Unknown
    }

    protected downloadDataObject(dataObject: TextureThumbnailViewDataObjectDetailsFragment): void {
        FilesService.downloadFile(dataObject.originalFileName, dataObject.downloadUrl)
    }

    protected describeDataObject(dataObject: TextureThumbnailViewDataObjectDetailsFragment): string {
        let typeName: string
        switch (dataObject.mediaType) {
            case "image/x-exr":
                typeName = "EXR"
                break
            case "image/tiff":
                typeName = "TIFF"
                break
            case "image/jpeg":
                typeName = "JPEG"
                break
            default:
                typeName = `${dataObject.mediaType}`
                break
        }
        const colorSpaceName = dataObject.imageColorSpace?.toString() ?? ImageColorSpace.Unknown.toString()
        return `${dataObject.width ?? "?"} x ${dataObject.height ?? "?"} ${typeName} (${colorSpaceName})`
    }

    protected async setOriginalColorspace(dataObject: TextureThumbnailViewDataObjectDetailsFragment, colorSpace: ImageColorSpace) {
        dataObject.imageColorSpace = colorSpace
        await mutateThrowingErrors(this.textureThumbnailViewUpdateDataObject)({input: {id: dataObject.id, imageColorSpace: colorSpace}})
        await this.generateThumbnailService.createThumbnailForDataObject(dataObject.id)
    }

    protected async regenerateThumbnails(dataObject: TextureThumbnailViewDataObjectDetailsFragment) {
        await this.generateThumbnailService.createThumbnailForDataObject(dataObject.id)
    }

    protected sortedRelatedDataObjects(dataObject: TextureThumbnailViewDataObjectFragment): TextureThumbnailViewDataObjectFragment["related"] {
        return [...dataObject.related].sort((a, b) => {
            const typeOrderA = MEDIA_TYPE_SORT_ORDER[a.mediaType ?? ""] ?? Number.MAX_SAFE_INTEGER
            const typeOrderB = MEDIA_TYPE_SORT_ORDER[b.mediaType ?? ""] ?? Number.MAX_SAFE_INTEGER
            if (typeOrderA !== typeOrderB) {
                return typeOrderA - typeOrderB
            }
            return (b.width ?? 0) * (b.height ?? 0) - (a.width ?? 0) * (a.height ?? 0)
        })
    }

    protected onFilesSelected(event: Event) {
        const inputElement = event.target as HTMLInputElement
        if (inputElement.files && inputElement.files.length > 0) {
            this.fileDropped.emit(inputElement.files[0])
        }
    }

    protected onFilesDropped(event: File[]) {
        if (event.length > 0) {
            this.fileDropped.emit(event[0])
        }
    }

    protected imageColorSpace = ImageColorSpace
    readonly $dataObject = signal<TextureThumbnailViewDataObjectFragment | undefined>(undefined)

    private _loadingNewTexture = new Subject<void>()
    private _dataObjectUpdated = new Subject<void>()
    private _dataObjectId: string | undefined = undefined
    private _isDestroyed = false
    protected readonly Settings = Settings
    protected readonly ThumbnailLayout = ThumbnailLayout
    protected readonly DownloadResolution = DownloadResolution
    protected readonly MimeType = MimeType
}
